/**
 * zoneUtils.ts — SINGLE SOURCE OF TRUTH for zone colors and metadata.
 *
 * Colors are loaded dynamically from the backend /api/zones endpoint (which
 * reads from the database). A static fallback is provided for first-render /
 * offline use so no component ever shows an empty state.
 *
 * ╔═══════════════════════════════════════════════════════════════╗
 * ║  DO NOT hardcode zone colors anywhere else in the codebase.  ║
 * ║  Import getZoneColor / ZONE_ORDER / useZones from here.      ║
 * ╚═══════════════════════════════════════════════════════════════╝
 */

import { useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "../config/api";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface ZoneMeta {
  id: number;
  name: string;
  color: string;       // hex background / main color
  textColor: string;   // hex text color
  status: boolean;
}

// ── DB-synced canonical order (highest wellbeing → lowest) ────────────────────
// Orange Zone is intentionally excluded — it is not in the pleasantness-based
// mood log mapping. It exists in the DB but is not displayed in the heatmap or
// zone lists. It is still in ZONE_FALLBACK for color lookup if needed.

export const ZONE_ORDER: string[] = [
  "Green Zone",
  "Blue Zone",
  "Yellow Zone",
  "Brown Zone",
  "Red Zone",
  "Dark Red",
];

// ── Static fallback (mirrors the DB values so first-render is correct) ─────────

export const ZONE_FALLBACK: Record<string, { color: string; textColor: string }> = {
  "Green Zone":  { color: "#10b981", textColor: "#064e3b" },  // DB: #10b981
  "Blue Zone":   { color: "#3b82f6", textColor: "#1e3a8a" },  // DB: #3b82f6
  "Yellow Zone": { color: "#f59e0b", textColor: "#78350f" },  // DB: #f59e0b
  "Orange Zone": { color: "#f97316", textColor: "#7c2d12" },  // DB: #f97316
  "Brown Zone":  { color: "#8b4513", textColor: "#451a03" },  // DB: #8b4513
  "Red Zone":    { color: "#ef4444", textColor: "#7f1d1d" },  // DB: #ef4444
  "Dark Red":    { color: "#dc2626", textColor: "#450a0a" },  // DB: #dc2626
};

// Cached after first fetch so we only hit the network once per page load
let _zoneCache: ZoneMeta[] | null = null;
let _fetchPromise: Promise<ZoneMeta[]> | null = null;

export async function fetchZones(): Promise<ZoneMeta[]> {
  if (_zoneCache) return _zoneCache;
  if (_fetchPromise) return _fetchPromise;

  _fetchPromise = axios
    .get<ZoneMeta[]>(`${API_BASE_URL}/zones`)
    .then((res) => {
      _zoneCache = res.data.filter((z: ZoneMeta) => z.status && z.name !== "Light Red" && z.name !== "Orange Zone");
      return _zoneCache;
    })
    .catch(() => {
      // Return fallback as ZoneMeta array on error
      return Object.entries(ZONE_FALLBACK).map(([name, v], i) => ({
        id: i + 1,
        name,
        color: v.color,
        textColor: v.textColor,
        status: true,
      }));
    });

  return _fetchPromise;
}

// ── Sync helper (uses cache or fallback immediately) ──────────────────────────

/**
 * Returns {color, textColor} for a zone name.
 * Works synchronously using the static fallback.
 * Automatically resolves short names like "Green" → "Green Zone".
 */
export function formatZoneName(zone: string | null | undefined): string {
  if (!zone) return "";
  let key = zone.trim();
  if (key === "Light Red") return "Red Zone";
  const specialNames = new Set(["Dark Red"]);
  if (!key.includes("Zone") && !specialNames.has(key)) {
    key = `${key} Zone`;
  }
  return key;
}

export function getZoneColor(zoneName: string | undefined | null): { color: string; textColor: string } {
  const key = formatZoneName(zoneName);
  if (!key) return { color: "#e5e7eb", textColor: "#6b7280" };

  if (_zoneCache) {
    const found = _zoneCache.find((z) => z.name === key);
    if (found && found.color) return { color: found.color, textColor: found.textColor };
  }

  return ZONE_FALLBACK[key] ?? { color: "#e5e7eb", textColor: "#6b7280" };
}

/**
 * Sort an array of zone-name strings in ZONE_ORDER sequence.
 */
export function sortZones(zones: string[]): string[] {
  return [...zones].sort((a, b) => {
    const ai = ZONE_ORDER.indexOf(a);
    const bi = ZONE_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
}

/**
 * Maps a numerical score (0-100) to the canonical 6-zone name (no Orange Zone).
 */
export function getScoreZone(score: number): string {
  if (score >= 86) return "Green Zone";
  if (score >= 71) return "Blue Zone";
  if (score >= 43) return "Yellow Zone";  // covers former Orange range too
  if (score >= 29) return "Brown Zone";
  if (score >= 14) return "Red Zone";
  return "Dark Red";
}

// ── React hook ────────────────────────────────────────────────────────────────

/**
 * useZones() — fetches zones from DB once, returns sorted active list.
 * Components can call getZoneColor() directly; they don't need to wait for this hook.
 */
export function useZones(): { zones: ZoneMeta[]; loading: boolean } {
  const [zones, setZones] = useState<ZoneMeta[]>(() => {
    // Immediate fallback so first render has data
    if (_zoneCache) return _zoneCache;
    return Object.entries(ZONE_FALLBACK).map(([name, v], i) => ({
      id: i + 1,
      name,
      color: v.color,
      textColor: v.textColor,
      status: true,
    }));
  });
  const [loading, setLoading] = useState(!_zoneCache);

  useEffect(() => {
    if (_zoneCache) return; // already loaded
    fetchZones().then((data) => {
      setZones(data);
      setLoading(false);
    });
  }, []);

  return { zones, loading };
}
